import {
  API,
  USER_CACHE_KEY
} from '@config/index';
import {
  TrainingPhraseConfig,
  FrameType,
  trainingPhraseToLocal,
  attributeToLocal,
  valueSuggestionToLocal,
  dataQueryToLocal,
  valueAssociationToLocal,
  valueCheckToLocal,
  confirmationInfoToLocal,
  originKeyToLocal,
  compositeInfoToLocal,
  Attribute,
  DataQuery,
  genericTypeToLocal,
  externalTypeToLocal,
  serviceSlotToLocal,
  stateUpdateInfoToLocal,
  frameMappingToLocal,
  frameStorageAnnotationToLocal,
  referenceNodeToLocal,
  dialogActToLocal,
  postFillActionToLocal,
  DefaultGenericTypes,
  genericTypesToLocal,
  EntityInfo,
  ParameterType,
  actionToLocal,
  ParameterConfig,
  augmentMetaToLocal
} from '@interface/index';
import { localizedString } from '@locale/locale';
import { defaultState, StateInfo } from './index';
import { Dispatch } from 'redux';
import { EffectDeclare } from '@lib/model-proxy';
import { message } from 'antd';
import { store } from '@reducer/index';
import { ParameterMapStore } from '@lib/parameter-map';
import { dataQueryToParameter, getAgentId, getFullName } from '@lib/index';
import { globalModel } from '@reducer/global';

export const effects: EffectDeclare = {};

effects.fetchCompositeAttributes = ({ id, agentId }: { id: string, agentId?: string }) => {
  return async (dispatch: Dispatch) => {

    const res: any = await API.composite.getItemService(id, agentId);

    const userInfo = JSON.parse(
      window.localStorage.getItem(USER_CACHE_KEY) || '{}'
    );
    const readonly = res.org === 'naturali' && userInfo.org !== 'naturali';
    const selectedParametersPayload: Attribute[] = [];
    const trainingPhrasePayload: TrainingPhraseConfig[] = [];
    const negativePhrasePayload: TrainingPhraseConfig[] = [];
    const dontCareExpressions: TrainingPhraseConfig[] = [];

    if (res.attributes && res.attributes.length) {
      const kvs: { [key: string]: ParameterConfig } = {};

      res.attributes.forEach((parameter: any) => {
        const target: Attribute | undefined = attributeToLocal(parameter);

        if (target) {
          selectedParametersPayload.push(target);
        }

        kvs[target.id || ''] = target;
      });

      ParameterMapStore.setMany(kvs);
    }

    ((res && res.frame_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      trainingPhrasePayload.push(tpItemPayload);
    });

    ((res && res.negative_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      negativePhrasePayload.push(tpItemPayload);
    });

    ((res && res.dont_care_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      dontCareExpressions.push(tpItemPayload);
    });

    // DataQueries
    const dataQueriesPayload: DataQuery[] = [];

    const kvs: { [key: string]: ParameterConfig } = {};

    (res.data_queries || []).forEach((item: any) => {
      const data: DataQuery = dataQueryToLocal(item);
      dataQueriesPayload.push(data);
      kvs[data.id || ''] = dataQueryToParameter(data);
    });
    ParameterMapStore.setMany(kvs);

    const payload = {
      ...defaultState,
      readonly,
      dontCareExpressions,
      selectedParameters: selectedParametersPayload,
      nestedSlotCustomizations: res.nested_slot_customizations && res.nested_slot_customizations.map(attributeToLocal),
      headSlot: referenceNodeToLocal(res.head_slot),
      trainingPhrases: trainingPhrasePayload,
      negativePhrases: negativePhrasePayload,
      id: res.id,
      scope: res.scope,
      name: res.name || '',
      comment: res.comment,
      source: res.source,
      originKey: res.origin_key && originKeyToLocal(res.origin_key),
      structKey: res.struct_key && originKeyToLocal(res.struct_key),
      parentId: res.parent_id || '',
      selectedCompositeInherit: (res.inherited_frames || []).map((item: any) => ({
        id: item.frame.id,
        name: item.frame.name,
        type: item.frame.type,
        state: item.state
      })),
      dataQueries: dataQueriesPayload,
      services: (res.services || []).map(serviceSlotToLocal),
      genericTypes: genericTypesToLocal(res.generic_types),
      externalTypes: (res.external_types || []).map(externalTypeToLocal),
      valueSuggestions: (res.value_suggestions || []).map(valueSuggestionToLocal),
      confirmations: (res.confirmations || []).map(confirmationInfoToLocal),
      valueChecks: (res.value_checks || []).map(valueCheckToLocal),
      stateUpdates: (res.state_updates || []).map(stateUpdateInfoToLocal),
      mappings: (res.mappings || []).map(frameMappingToLocal),
      dialogActs: (res.dialog_acts || []).map(dialogActToLocal),
      postFillActions: (res.post_fill_actions || []).map(postFillActionToLocal),
      triggers: res.triggers || [],
      description: res.description,
      qualification: res.qualification,
      externalQualification: res.external_qualification,
      sourceType: res.source_type,
      weak: res.weak,
      isInterface: res.is_interface,
      cuiDisabled: res.cui_disabled,
      associateType: res.associate_type && {
        id: res.associate_type.id,
        name: res.associate_type.name,
        type: res.associate_type.type
      },
      subOfWeakTypeId: res.sub_of_weak_type_id,
      useExpression: res.use_expression && {
        id: res.use_expression.id,
        name: res.use_expression.name,
        type: res.use_expression.type
      },
      isGlobal: res.is_global || false,
      storageEnable: res.storage_enable || false,
      storageAnnotation: frameStorageAnnotationToLocal(res.storage_annotation),
      importedFromService: res.imported_from_service,
      booleanGatePrompts: res.boolean_gate_prompts,
      booleanGateExpressionsYes: res.boolean_gate_expressions_yes &&
        res.boolean_gate_expressions_yes.map(trainingPhraseToLocal),
      booleanGateExpressionsNo: res.boolean_gate_expressions_no &&
        res.boolean_gate_expressions_no.map(trainingPhraseToLocal),
      enableFallback: res.enable_fallback || false,
      fallbackInfo: res.fallback_info && augmentMetaToLocal(res.fallback_info)
    };


    setTimeout(() => {
      const parsedComposite = compositeInfoToLocal(res);
      store.dispatch({
        payload: parsedComposite,
        type: 'global/composite_update'
      });

      dispatch({
        payload,
        type: 'composite/update_state'
      });

      window.sessionStorage.setItem('curFrame', JSON.stringify({
        id: payload.id,
        name: payload.name,
        frameType: FrameType.composite,
        attributes: [
          ...payload.selectedParameters,
          ...payload.dataQueries
        ].map((attr: any) =>
          attr.id)
      }));

      // force update neccessary fields
      const param = ParameterMapStore.get(payload.id);
      ParameterMapStore.set(payload.id, {
        ...param,
        description: payload.description
      });
    }, 1000);

    return payload;
  };
};

effects.fetchParameters = ({ id, agentId }: { id: string, agentId?: string }) => {
  return async (dispatch: Dispatch) => {

    const { parameterPayload }: any = await dispatch<any>(
      globalModel.effects.fetchParameters({ id: agentId || getAgentId() })
    );

    return parameterPayload;
  };
};

effects.patchCompositeAttribute = (
  { id, data }: { id: string; data: any },
  callback?: (res: any) => void
) => {

  return async (dispatch: Dispatch) => {
    const res: any = await API.composite
      .updateItemService({ metaID: id }, data)
      .catch((err: any) => {
        message.warning(err.message || localizedString('common_save_failed'));
        throw new Error(err.message);
      });

    if (callback) {
      callback(res);
    }
  };
};
